/**
 * ImageUploadField Component
 * Allows users to upload up to 3 images with preview, validation, and Supabase Storage integration
 */

import React, { useRef, useState, useEffect } from 'react';
import { Upload, X, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { Label } from '../ui/Label';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { logger } from '../../utils/logger';

interface ImageUploadFieldProps {
  /** Array of uploaded image URLs */
  value: string[];
  /** Callback when images change */
  onChange: (urls: string[]) => void;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Maximum number of images allowed */
  maxImages?: number;
  /** Maximum file size in bytes */
  maxFileSize?: number;
}

interface UploadingImage {
  id: string;
  file: File;
  preview: string;
  progress: number;
  error?: string;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
const MAX_IMAGES = 3;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

export const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
  value = [],
  onChange,
  disabled = false,
  maxImages = MAX_IMAGES,
  maxFileSize = MAX_FILE_SIZE,
}) => {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingImages, setUploadingImages] = useState<UploadingImage[]>([]);
  const [error, setError] = useState<string>('');

  // Cleanup object URLs on unmount
  useEffect(() => {
    const currentImages = [...uploadingImages];
    return () => {
      currentImages.forEach(img => URL.revokeObjectURL(img.preview));
    };
  }, [uploadingImages]);

  const validateFile = (file: File): string | null => {
    // Check file type
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      return 'Please upload only image files (JPEG, PNG, GIF, or WebP)';
    }

    // Check file size
    if (file.size > maxFileSize) {
      const maxSizeMB = maxFileSize / (1024 * 1024);
      return `Image exceeds ${maxSizeMB}MB limit. Please choose a smaller file.`;
    }

    return null;
  };

  const generateFileName = (file: File): string => {
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2, 9);
    const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    return `${timestamp}-${randomId}.${extension}`;
  };

  const uploadToSupabase = async (file: File, uploadId: string): Promise<string | null> => {
    if (!user) {
      throw new Error('User must be authenticated to upload images');
    }

    try {
      const fileName = generateFileName(file);
      const filePath = `${user.id}/${fileName}`;

      logger.debug('Uploading image to Supabase Storage:', { filePath, size: file.size });

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('feedback-attachments')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        logger.error('Supabase upload error:', uploadError);
        throw new Error(uploadError.message);
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('feedback-attachments')
        .getPublicUrl(filePath);

      logger.debug('Image uploaded successfully:', { publicUrl });

      return publicUrl;
    } catch (error) {
      logger.error('Error uploading image:', error);

      // Update uploading image with error
      setUploadingImages(prev =>
        prev.map(img =>
          img.id === uploadId
            ? { ...img, error: error instanceof Error ? error.message : 'Upload failed' }
            : img
        )
      );

      return null;
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setError('');

    // Check if adding these files would exceed max images
    const totalImages = value.length + uploadingImages.length + files.length;
    if (totalImages > maxImages) {
      setError(`Maximum ${maxImages} images allowed`);
      return;
    }

    // Validate and prepare files for upload
    const validFiles: UploadingImage[] = [];

    for (const file of files) {
      const validationError = validateFile(file);

      if (validationError) {
        setError(validationError);
        continue;
      }

      const uploadId = Math.random().toString(36).substring(2, 9);
      const preview = URL.createObjectURL(file);

      validFiles.push({
        id: uploadId,
        file,
        preview,
        progress: 0,
      });
    }

    if (validFiles.length === 0) {
      return;
    }

    // Add to uploading state
    setUploadingImages(prev => [...prev, ...validFiles]);

    // Upload files
    const uploadPromises = validFiles.map(async (uploadingImage) => {
      const url = await uploadToSupabase(uploadingImage.file, uploadingImage.id);
      return { uploadId: uploadingImage.id, url };
    });

    const results = await Promise.all(uploadPromises);

    // Update state: remove uploading images and add successful URLs
    const successfulUrls = results.filter(r => r.url !== null).map(r => r.url as string);

    setUploadingImages(prev => prev.filter(img => !results.some(r => r.uploadId === img.id)));

    if (successfulUrls.length > 0) {
      onChange([...value, ...successfulUrls]);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = async (urlToRemove: string) => {
    // Remove from state
    onChange(value.filter(url => url !== urlToRemove));

    // Optionally delete from Supabase Storage
    // Extract file path from URL
    try {
      const url = new URL(urlToRemove);
      const pathMatch = url.pathname.match(/\/object\/public\/feedback-attachments\/(.+)$/);

      if (pathMatch && pathMatch[1] && user) {
        const filePath = pathMatch[1];

        // Only delete if the file belongs to this user (safety check)
        if (filePath.startsWith(`${user.id}/`)) {
          const { error } = await supabase.storage
            .from('feedback-attachments')
            .remove([filePath]);

          if (error) {
            logger.warn('Failed to delete image from storage:', error);
          }
        }
      }
    } catch (error) {
      logger.warn('Error parsing image URL for deletion:', error);
    }
  };

  const handleRemoveUploadingImage = (uploadId: string) => {
    setUploadingImages(prev => {
      const image = prev.find(img => img.id === uploadId);
      if (image) {
        URL.revokeObjectURL(image.preview);
      }
      return prev.filter(img => img.id !== uploadId);
    });
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const canAddMore = value.length + uploadingImages.length < maxImages;
  const hasImages = value.length > 0 || uploadingImages.length > 0;

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <Label>
          Attach Images (Optional)
        </Label>
        <span className="text-xs text-muted-foreground">
          {value.length + uploadingImages.length}/{maxImages}
        </span>
      </div>

      {/* Upload Button */}
      {canAddMore && (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_IMAGE_TYPES.join(',')}
            multiple
            onChange={handleFileSelect}
            disabled={disabled}
            className="hidden"
          />
          <Button
            type="button"
            variant="outline"
            onClick={handleButtonClick}
            disabled={disabled || !canAddMore}
            className="w-full"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload Images ({value.length}/{maxImages})
          </Button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-start gap-2 p-3 text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/20">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {/* Image Previews */}
      {hasImages && (
        <div className="grid grid-cols-3 gap-3">
          {/* Uploaded Images */}
          {value.map((url, index) => (
            <div
              key={url}
              className="relative aspect-square rounded-lg border-2 border-border overflow-hidden bg-muted group"
            >
              <img
                src={url}
                alt={`Attachment ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => handleRemoveImage(url)}
                disabled={disabled}
                className="absolute top-1 right-1 p-1 bg-destructive text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                aria-label="Remove image"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}

          {/* Uploading Images */}
          {uploadingImages.map((uploadingImage) => (
            <div
              key={uploadingImage.id}
              className="relative aspect-square rounded-lg border-2 border-border overflow-hidden bg-muted"
            >
              <img
                src={uploadingImage.preview}
                alt="Uploading..."
                className="w-full h-full object-cover"
              />

              {/* Loading Overlay */}
              {!uploadingImage.error && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <Loader2 className="h-6 w-6 text-white animate-spin" />
                </div>
              )}

              {/* Error Overlay */}
              {uploadingImage.error && (
                <div className="absolute inset-0 bg-destructive/90 flex flex-col items-center justify-center p-2">
                  <AlertCircle className="h-5 w-5 text-destructive-foreground mb-1" />
                  <p className="text-xs text-destructive-foreground text-center">
                    Upload failed
                  </p>
                  <button
                    type="button"
                    onClick={() => handleRemoveUploadingImage(uploadingImage.id)}
                    className="mt-2 text-xs underline text-destructive-foreground"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Help Text */}
      <p className="text-xs text-muted-foreground">
        Add screenshots or images to help explain your feedback. Max {maxImages} images, {maxFileSize / (1024 * 1024)}MB each.
      </p>
    </div>
  );
};
